import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Control } from "react-hook-form";

import { Button } from "@/components/ui/button";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
  ClientsKeys,
  getLicenseeClients,
} from "@/features/clients/api/clients";
import AddContactSheet from "@/features/clients/components/AddClientSheet";

import { InvoiceFormValues } from "./types";

interface CustomerInformationProps {
  control: Control<InvoiceFormValues>;
  disabled?: boolean;
}

const CustomerInformation = ({
  control,
  disabled,
}: CustomerInformationProps) => {
  const [isAddCustomerOpen, setIsAddCustomerOpen] = useState(false);

  const { data: clients = [], isLoading } = useQuery({
    queryKey: [ClientsKeys.Clients],
    queryFn: getLicenseeClients,
  });

  const getCustomerDisplayValue = (customerId: string) => {
    const customer = clients.find((c) => c.id === customerId);
    if (!customer) {
      // If customer not found in the list, show at least the ID
      return (
        <div className="flex flex-col gap-0.5">
          <span className="font-medium text-base">Customer {customerId}</span>
          <span className="text-sm text-muted-foreground">
            Loading details...
          </span>
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-0.5 min-w-0 max-w-[calc(100%-8px)]">
        {customer.company ? (
          <>
            <span className="font-medium text-sm truncate">{customer.company}</span>
            <span className="text-xs text-muted-foreground truncate">
              {customer.name} • {customer.email}
            </span>
          </>
        ) : (
          <>
            <span className="font-medium text-sm truncate">{customer.name}</span>
            <span className="text-xs text-muted-foreground truncate">
              {customer.email}
            </span>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="grid gap-4">
        <FormField
          control={control}
          name="customerId"
          render={({ field }) => (
            <FormItem className="space-y-2">
              <FormLabel>Select Customer</FormLabel>
              <Select
                onValueChange={field.onChange}
                value={field.value}
                disabled={isLoading || disabled}
              >
                <FormControl>
                  <SelectTrigger multiline className="min-h-[3.5rem] w-full py-2 text-left">
                    <SelectValue placeholder="Select a client">
                      {field.value && getCustomerDisplayValue(field.value)}
                    </SelectValue>
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <Button
                    variant="ghost"
                    className="relative w-full justify-start rounded-none font-normal hover:bg-sds-neutral-00"
                    onClick={() => setIsAddCustomerOpen(true)}
                    disabled={disabled}
                  >
                    Add New Client
                  </Button>
                  <Separator className="my-2" />
                  {clients.map((client) => (
                    <SelectItem
                      key={client.id}
                      value={client.id}
                      className="py-2"
                    >
                      <div className="flex flex-col gap-0.5">
                        <span className="font-medium text-base">
                          {client.company || client.name}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          {client.company
                            ? `${client.name} • ${client.email}`
                            : client.email}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <AddContactSheet
        isOpen={isAddCustomerOpen}
        onClose={() => setIsAddCustomerOpen(false)}
      />
    </div>
  );
};

export default CustomerInformation;
